import { useEffect, useState, type FormEvent } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useCreateLog, usePatchLog } from '@/lib/queries';
import { useDomains, useZones } from '@/lib/queries';
import type { LogEntityType, MaintenanceLog } from '@/lib/types';

/**
 * Sheet-hosted log entry form. Two modes:
 *
 *   <LogForm open={…} onOpenChange={…} mode="create" defaults={…} />
 *   <LogForm open={…} onOpenChange={…} mode="edit" log={existing} />
 *
 * Mobile-forward by default — full-width sheet, large fields, native
 * datetime-local picker. Notes are a real `<textarea>`.
 *
 * Cost is stored as integer cents (a JS-money discipline) but
 * presented in dollars to the user.
 */

export interface LogFormDefaults {
  domainId?: string;
  zoneId?: string | null;
  entityType?: LogEntityType | null;
  entityId?: string | null;
  title?: string;
  performedAt?: string;
  notes?: string | null;
}

type Props =
  | {
      open: boolean;
      onOpenChange: (next: boolean) => void;
      mode: 'create';
      defaults?: LogFormDefaults;
      onCreated?: (log: MaintenanceLog) => void;
    }
  | {
      open: boolean;
      onOpenChange: (next: boolean) => void;
      mode: 'edit';
      log: MaintenanceLog;
      onSaved?: (log: MaintenanceLog) => void;
    };

export default function LogForm(props: Props) {
  const { open, onOpenChange, mode } = props;
  const create = useCreateLog();
  const patch = usePatchLog(mode === 'edit' ? props.log.id : '');
  const domainsQ = useDomains();
  const zonesQ = useZones();

  const initial =
    mode === 'edit'
      ? {
          domainId: props.log.domainId,
          zoneId: props.log.zoneId,
          entityType: props.log.entityType,
          entityId: props.log.entityId,
          title: props.log.title,
          notes: props.log.notes ?? '',
          performedAt: toLocalInput(props.log.performedAt),
          costDollars: props.log.cost == null ? '' : (props.log.cost / 100).toFixed(2),
        }
      : {
          domainId: props.defaults?.domainId ?? '',
          zoneId: props.defaults?.zoneId ?? '',
          entityType: props.defaults?.entityType ?? null,
          entityId: props.defaults?.entityId ?? null,
          title: props.defaults?.title ?? '',
          notes: props.defaults?.notes ?? '',
          performedAt:
            props.defaults?.performedAt != null
              ? toLocalInput(props.defaults.performedAt)
              : toLocalInput(new Date().toISOString()),
          costDollars: '',
        };

  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setError(null);
    }
    // intentional shallow dep — re-init only when sheet opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!form.domainId) {
      setError('Pick a domain.');
      return;
    }
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    const performedAt = fromLocalInput(form.performedAt);
    const cost =
      form.costDollars.trim() === ''
        ? null
        : Math.max(0, Math.round(Number.parseFloat(form.costDollars) * 100));

    try {
      if (mode === 'create') {
        const created = await create.mutateAsync({
          domainId: form.domainId,
          zoneId: form.zoneId || null,
          entityType: form.entityType ?? null,
          entityId: form.entityId ?? null,
          performedAt,
          title: form.title.trim(),
          notes: form.notes.trim() === '' ? null : form.notes,
          cost: Number.isFinite(cost as number) ? (cost as number | null) : null,
        });
        props.onCreated?.(created);
      } else {
        const saved = await patch.mutateAsync({
          domainId: form.domainId,
          zoneId: form.zoneId || null,
          entityType: form.entityType ?? null,
          entityId: form.entityId ?? null,
          performedAt,
          title: form.title.trim(),
          notes: form.notes.trim() === '' ? null : form.notes,
          cost: Number.isFinite(cost as number) ? (cost as number | null) : null,
        });
        props.onSaved?.(saved);
      }
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'New log entry' : 'Edit log'}</SheetTitle>
          <SheetDescription>
            Capture what you did so future-you knows when, why, and with what.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={onSubmit} className="space-y-4 pt-4 pb-2" data-testid="log-form">
          <div className="space-y-1.5">
            <Label htmlFor="title" className="font-mono text-[11px] uppercase tracking-wider">
              Title
            </Label>
            <Input
              id="title"
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              autoFocus
              className="h-11 text-base"
              data-testid="log-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="performedAt" className="font-mono text-[11px] uppercase tracking-wider">
                Performed
              </Label>
              <Input
                id="performedAt"
                type="datetime-local"
                required
                value={form.performedAt}
                onChange={(e) => setForm((f) => ({ ...f, performedAt: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cost" className="font-mono text-[11px] uppercase tracking-wider">
                Cost (USD)
              </Label>
              <Input
                id="cost"
                type="number"
                inputMode="decimal"
                step="0.01"
                min={0}
                placeholder="0.00"
                value={form.costDollars}
                onChange={(e) => setForm((f) => ({ ...f, costDollars: e.target.value }))}
                className="h-11"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="domain" className="font-mono text-[11px] uppercase tracking-wider">
              Domain
            </Label>
            <select
              id="domain"
              required
              value={form.domainId}
              onChange={(e) => setForm((f) => ({ ...f, domainId: e.target.value }))}
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="log-domain"
            >
              <option value="">Select a domain…</option>
              {(domainsQ.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="zone" className="font-mono text-[11px] uppercase tracking-wider">
              Zone (optional)
            </Label>
            <select
              id="zone"
              value={form.zoneId ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, zoneId: e.target.value || null }))}
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">No zone</option>
              {(zonesQ.data ?? []).map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes" className="font-mono text-[11px] uppercase tracking-wider">
              Notes
            </Label>
            <textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={5}
              placeholder="What happened? Anything to remember next time?"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>

          {error ? (
            <p className="text-sm text-warning font-medium" role="alert">
              {error}
            </p>
          ) : null}

          <div className="sticky bottom-0 -mx-5 px-5 pt-3 pb-1 bg-card border-t border-rule flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || patch.isPending} data-testid="log-submit">
              {(create.isPending || patch.isPending) ? 'Saving…' : mode === 'create' ? 'Save log' : 'Save changes'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/** Convert ISO → `YYYY-MM-DDTHH:MM` (local) for `datetime-local`. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `YYYY-MM-DDTHH:MM` (local) → ISO string. */
function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}
